import { ReactNode } from 'react';

import { useForm, useFormState } from 'react-final-form';

import {
  LabeledRadio,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { Cloud } from '@cast/shared-types';

import { CloudSettingsSection } from './CloudSettingsSection';
import { CUSTOM_EDGE_PROVIDER } from '../constants';
import { EdgeLocationProvider } from '../types';

type OptionCardProps = {
  cardKey: string;
  isSelected: boolean;
  label: string;
  description: string;
  defaultProvider: EdgeLocationProvider;
  children?: ReactNode;
};

const OptionCard = ({
  cardKey,
  defaultProvider,
  isSelected,
  label,
  description,
  children,
}: OptionCardProps) => {
  const { batch, change } = useForm();

  return (
    <Box
      component={isSelected ? 'div' : 'label'}
      data-testid={`edge-location-type-${cardKey}`}
      sx={{
        border: '1px solid',
        borderColor: isSelected
          ? tokens.theme.border.selection.primary
          : tokens.theme.border.structural.neutral,
        borderRadius: 1,
        backgroundColor: tokens.theme.bg.structural.surface,
        p: 16,
        cursor: 'pointer',
      }}
    >
      <LabeledRadio
        radioProps={{
          checked: isSelected,
          onChange: () => {
            batch(() => {
              change('provider', defaultProvider);
              change('region', undefined);
              change('projectId', '');
              change('locationName', '');
            });
          },
          testId: `edge-location-type-${cardKey}-radio`,
        }}
        label={label}
        size="md"
      />
      <Typography
        variant="P12R"
        color="textSecondary"
        sx={{ pl: 28, mt: 4, display: 'block' }}
      >
        {description}
      </Typography>
      {isSelected && children && (
        <Stack mt={12} pl={28}>
          {children}
        </Stack>
      )}
    </Box>
  );
};

export const LocationSettingsSection = () => {
  const { values } = useFormState({ subscription: { values: true } });

  const isCloudSelected =
    values.provider && values.provider !== CUSTOM_EDGE_PROVIDER;
  const isCustomSelected =
    values.provider && values.provider === CUSTOM_EDGE_PROVIDER;

  return (
    <Stack gap={16}>
      <OptionCard
        cardKey="cloud"
        defaultProvider={Cloud.aws}
        isSelected={isCloudSelected}
        label="Extend to a cloud service provider"
        description="Choose a cloud provider and region to provision compute capacity."
      >
        <CloudSettingsSection />
      </OptionCard>

      <OptionCard
        cardKey={CUSTOM_EDGE_PROVIDER}
        defaultProvider={CUSTOM_EDGE_PROVIDER}
        isSelected={isCustomSelected}
        label="Extend to a custom provider"
        description="Requires network connectivity between your provider and the cluster."
      />
    </Stack>
  );
};
